import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App.jsx'

// ---------------------------------------------------------------------------
// Global HTTP layer — four patches installed in strict order.
//
// Every HTTP request the app makes (fetch API or XHR/axios) passes through
// a stack of transparent wrappers before reaching the network. The order of
// the install calls below determines which wrapper runs first:
//
//   installFetchPatch()     — innermost fetch wrapper: adds Drupal credentials
//                             and CSRF tokens to mutating requests; redirects
//                             to login on 401 (session expired).
//   installXhrPatch()       — same responsibilities for XHR (used by axios /
//                             ReactiveJSON), and wires batcher + cache for XHR.
//   installRequestBatcher() — groups concurrent GET requests into a single
//                             Drupal /subrequests call to reduce round-trips.
//   installRequestCache()   — outermost fetch wrapper: serves repeated GETs
//                             from memory, short-circuiting everything below.
//
// The cache must be outermost so a hit skips the batcher and the network.
// The batcher must sit between cache and CSRF so real network calls are
// still authenticated when a cache miss falls through to the network.
// ---------------------------------------------------------------------------
import { installFetchPatch } from './services/fetchPatch.js'
import { installXhrPatch }from './services/xhrPatch.js'
import { installRequestBatcher } from './services/requestBatcher.js'
import { installRequestCache, primeRequestCache } from './services/requestCache.js'
import { holdSsgPaint } from './services/ssgPaint.js'

// Page pré-générée (SSG) : elle porte les réponses qui ont servi à la produire.
// On les charge AVANT d'installer quoi que ce soit — le premier rendu client les
// consomme au lieu de refaire la cascade resolve → catalogue → page, et le
// contenu déjà affiché ne disparaît pas le temps des allers-retours.
const ssgSeed = document.getElementById('__ORBIT_SSG_DATA__');
if (ssgSeed) {
  try { primeRequestCache(JSON.parse(ssgSeed.textContent)); }
  catch (e) { console.warn('SSG: données inlinées illisibles', e); }
  // Le contenu déjà peint reste à l'écran jusqu'à ce que l'app rende le sien :
  // React démarre dans un conteneur vide et n'a donc rien à effacer.
  holdSsgPaint(document.getElementById('orbit_front_app'));
}

installFetchPatch();
installXhrPatch();
installRequestBatcher();
installRequestCache();

// Runtime atomic-CSS for builder blocks served from Drupal content (not exported
// to config/builder/*.yaml). Tailwind v4 only scans build-time sources, so any
// class authored in a Drupal-stored block would otherwise have no CSS. UnoCSS
// observes DOM mutations and emits missing utility rules on the fly. The
// `@theme` tokens of index.css are mirrored in `./uno.config.js` so both
// engines resolve the same custom colors (navy-*, brand-blue, …) identically.
import initUno from '@unocss/runtime'
import unoConfig from './uno.config.js'
initUno({ defaults: unoConfig })

// Le corollaire des noms empreintés : après un déploiement, les fichiers de
// l'ancien build ont disparu. Un onglet resté ouvert garde, lui, le bundle
// précédent — et le premier écran chargé à la demande (import dynamique) tente
// alors une adresse qui n'existe plus. Vite signale cet échec précis par
// `vite:preloadError` ; on recharge, ce qui va rechercher l'index et donc la
// version courante. Sans ça, l'écran ne s'ouvre simplement pas, et rien ne dit
// pourquoi.
//
// Garde-fou contre la boucle : on ne recharge qu'une fois par minute. Un
// rechargement qui ne règle rien (fichier réellement absent du build, et non
// déploiement) laisse alors l'erreur remonter, visible, au lieu de faire
// clignoter la page ; un onglet qui traverse deux déploiements successifs,
// lui, est bien rattrapé les deux fois.
window.addEventListener('vite:preloadError', (event) => {
  const CLE = 'orbit:preload-reload'
  const DELAI_MS = 60_000
  let dernier = 0
  // sessionStorage lève en navigation privée stricte : l'indisponibilité du
  // garde-fou ne doit pas empêcher le rechargement lui-même.
  try { dernier = Number(sessionStorage.getItem(CLE)) || 0 } catch { /* sans stockage */ }
  if (Date.now() - dernier < DELAI_MS) return
  event.preventDefault()
  try { sessionStorage.setItem(CLE, String(Date.now())) } catch { /* sans stockage */ }
  window.location.reload()
})

ReactDOM.createRoot(document.getElementById('orbit_front_app')).render(
  <App />
)
